'use client'

import { useState, useEffect } from 'react'
import { AnimatePresence } from 'motion/react'

import BlogPostContainer from '@/components/content/blog/post/BlogPostContainer'
import Portal from '@/components/ui/Portal'
import AuthModal from '@/components/authentication/AuthModal'

import { Icon } from '@/icons/Icon'
import type { App, Blog, Category } from '@/payload-types'

type GatedBlogContentProps = {
  slug: string
  blog: Pick<
    Blog,
    | 'id'
    | 'name'
    | 'slug'
    | 'blogApps'
    | 'blogCategories'
    | 'post'
    | 'isFeatured'
    | 'summary'
    | 'createdAt'
    | 'authors'
  >
  apps: App[]
  category?: Category
  layout?: 'standard' | 'editorial'
  modifiedTimestamp: string
}

type AuthState = 'checking' | 'authenticated' | 'unauthenticated'

export default function GatedBlogContent({
  slug,
  blog,
  apps,
  category,
  layout,
  modifiedTimestamp,
}: GatedBlogContentProps) {
  const [authState, setAuthState] = useState<AuthState>('checking')
  const [auth, setAuth] = useState<'signin' | 'signup' | null>(null)

  useEffect(() => {
    async function checkAuth() {
      try {
        const res = await fetch('/api/auth/session')
        const session = await res.json()
        setAuthState(session?.user?.id ? 'authenticated' : 'unauthenticated')
      } catch {
        setAuthState('unauthenticated')
      }
    }
    checkAuth()
  }, [])

  const paywall = authState !== 'authenticated'

  const blogContent = (
    <BlogPostContainer
      blog={blog}
      apps={apps}
      category={category}
      layout={layout}
      modifiedTimestamp={modifiedTimestamp}
      paywall={paywall}
    />
  )

  // Authenticated: lift the paywall and render the full post
  if (!paywall) {
    return blogContent
  }

  const isLoading = authState === 'checking'

  return (
    <>
      <div className="relative max-h-[600px] overflow-hidden md:max-h-[800px]">
        {blogContent}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-40 h-72 bg-gradient-to-t from-white from-20% via-white/95 via-50% to-transparent dark:from-dark-950 dark:from-20% dark:via-dark-950/95 dark:via-50%" />
      </div>

      <div className="flex flex-col items-center gap-5 px-4 pb-16 pt-8 text-center">
        {isLoading ? (
          <Icon name="loader" size={24} strokeWidth={1.5} className="animate-spin text-gray-400" />
        ) : (
          <>
            <h2 className="text-22 font-semibold text-gray-950 dark:text-white">
              Create a free account to keep reading.
            </h2>
            <span
              onClick={() => setAuth('signup')}
              className="mt-1 flex h-11 cursor-pointer items-center justify-center rounded-full bg-gray-950 px-8 text-14 font-semibold text-white transition-colors hover:bg-gray-800 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-100"
            >
              Sign up for free
            </span>
            <p className="text-13 text-gray-400 dark:text-dark-500">
              Already have an account?{' '}
              <span
                onClick={() => setAuth('signin')}
                className="cursor-pointer text-gray-500 underline transition-colors hover:text-gray-950 dark:text-dark-300 dark:hover:text-white"
              >
                Sign in
              </span>
            </p>
          </>
        )}
      </div>

      <Portal>
        <AnimatePresence>
          {auth && (
            <AuthModal
              close={() => setAuth(null)}
              redirectUrl={`/blog/${slug}`}
              config={{
                signUp: auth === 'signup',
              }}
            />
          )}
        </AnimatePresence>
      </Portal>
    </>
  )
}
